/**
 * The look the pe3d editor shares with the snapshot editor and the
 * versionedPlayEngine panels, in one place, so every pe3d pane and dialog
 * wears the same rules:
 *
 *   surfaces     panelPalette (PANEL_BG panes and dialogs, BAR_BG bars and
 *                raised cards, SECTION_DIVIDER hairlines)
 *   inspector    SectionHeader / FieldRow and the number, string and colour
 *                fields of Version Settings (containerPlayEngineVersionSettings),
 *                inside a `.proto-props-panel` (PROPS_PANEL_CSS)
 *   lists        the snapshot list's row grid and highlights
 *                (snapshotEditor/containerSnapshotList.js)
 *   cards        the snapshot Assets tab's raised cards and outlined buttons
 *                (snapshotEditor/containerAssets.js)
 *   dialogs      PANEL_BG paper, MUI fields 12px apart, Close/Cancel then one
 *                contained primary verb (snapshotEditor/dialogExport.js)
 */

import React from "react";
import { Typography } from "@material-ui/core";
import { BAR_BG, CONTROL_BG, CONTROL_BORDER, FIELD_LABEL, PANEL_BG, SECTION_DIVIDER } from "../snapshotEditor/panelPalette";

export {
  SectionHeader,
  FieldRow,
  StringConfigField,
  ColorConfigField,
  NumberConfigField,
} from "../containerPlayEngineVersionSettings";

export const MUTED = "#8a8d93";
export const TEXT = "#eee";
export const ERROR_TEXT = "#e57373";
export const WARNING_TEXT = "#e0b96a";
export const OK_TEXT = "#a5d6a7";

// Row highlights of the snapshot list and the component tree.
export const ROW_HOVER = "rgba(255, 255, 255, 0.055)";
export const ROW_SELECTED = "rgba(144, 202, 249, 0.20)";
export const ROW_SELECTED_HOVER = "rgba(144, 202, 249, 0.26)";

export const CARD_STYLE = { backgroundColor: BAR_BG, border: `1px solid ${SECTION_DIVIDER}`, boxShadow: "none" };
export const OUTLINED_BUTTON_STYLE = { color: FIELD_LABEL, backgroundColor: CONTROL_BG, border: `1px solid ${CONTROL_BORDER}`, textTransform: "none" };
export const DIALOG_PAPER_PROPS = { style: { backgroundColor: PANEL_BG } };
export const MENU_PAPER_PROPS = { style: { backgroundColor: BAR_BG, color: FIELD_LABEL } };

/** Dialog text styles (snapshotEditor/dialogExport.js). */
export const DIALOG_TEXT = {
  field: { marginTop: 12 },
  // Plainly secondary: a step dimmer than a field label, so it never competes with a caption.
  hint: { fontSize: 12, color: "#9296a0", lineHeight: 1.6, marginTop: 10 },
  headline: { fontSize: 14, fontWeight: 600, color: "#e2e5ea", marginTop: 10 },
  warning: { fontSize: 12, color: WARNING_TEXT, marginTop: 4 },
  failure: { fontSize: 12, color: "#ef9a9a", marginTop: 8 },
  busy: { display: "flex", alignItems: "center", gap: 10, marginTop: 12, fontSize: 13 },
  waitLine: { display: "block", fontSize: 12, color: FIELD_LABEL, marginTop: 2 },
};

/**
 * A caption over a block of a dialog (a column, a list, a preview), and the
 * quiet note at its right end. Bold, bright small capitals: the lists and hints
 * under it are 11–13px greys, and a caption in the same grey reads as one more
 * line of them rather than as what the lines belong to.
 */
export const DIALOG_CAPTION = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1,
  textTransform: "uppercase",
  color: "#f1f3f5",
  marginBottom: 8,
};
export const DIALOG_CAPTION_NOTE = { fontWeight: 400, fontSize: 11, textTransform: "none", letterSpacing: 0, color: MUTED, fontVariantNumeric: "tabular-nums" };

/** A panel's quiet line: what is missing and where to get it. */
export function PanelMessage({ children, style }) {
  return (
    <Typography variant="body2" style={{ color: MUTED, fontSize: 13, padding: "16px 10px", ...style }}>
      {children}
    </Typography>
  );
}

/** Inspector hint under a section (11px, muted). */
export function PanelHint({ children, style }) {
  return <div style={{ fontSize: 11, color: MUTED, lineHeight: 1.5, padding: "2px 6px 6px", ...style }}>{children}</div>;
}

/** A problem under a field (the validator's words). */
export function FieldProblem({ children }) {
  return <div style={{ fontSize: 11, color: ERROR_TEXT, padding: "0 6px 4px 110px", wordBreak: "break-word" }}>{children}</div>;
}
